import { makeAutoObservable, runInAction } from 'mobx';
import { BACKEND_MODULE_TYPES, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
import { setConfirmationActionType } from 'services/authUtils';
import {
  loginUser,
  loginPin,
  changePasswordUser,
  resendSecurityCode,
  getSmsCodeForce,
  forceChangeConfirmationTypeRequest
} from 'services/requestAgent';

class AuthStore {
  isLoading = false;
  isLoginSuccess = false;
  isUserAlreadyRegistered = false;
  isPinSuccess = false;
  isPasswordChanged = false;
  isShowSwitchConfirmationMethodScreen = false;
  error = null;
  confirmationActionType = null;
  confirmationId = null;
  isGoogle2FAEnabled = false;
  phone = null;

  values = {
    phone: process.env.REACT_APP_DEFAULT_DIAL_CODE,
    username: '',
    password: '',
    code: ''
  };

  constructor() {
    makeAutoObservable(this);
  }

  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
  }

  setIsLoginSuccess(status) {
    this.isLoginSuccess = status;
  }

  setIsPinSuccess(status) {
    this.isPinSuccess = status;
  }

  setIsUserAlreadyRegistered(status) {
    this.isUserAlreadyRegistered = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setUsername(username) {
    this.values.username = username;
  }

  setPhone(phone) {
    this.values.phone = phone;
  }

  setPassword(password) {
    this.values.password = password;
  }

  setCode(code) {
    this.values.code = code;
  }

  setConfirmationId(id) {
    this.confirmationId = id;
  }

  setUserAuthorizationData(confirmationId, phone, confirmationType) {
    this.isUserAlreadyRegistered = true;
    this.isLoginSuccess = true;
    this.confirmationId = confirmationId;
    this.phone = phone;
    this.confirmationActionType = confirmationType;
    this.isGoogle2FAEnabled = confirmationType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
  }

  reset() {
    this.isLoading = false;
    this.isLoginSuccess = false;
    this.isUserAlreadyRegistered = false;
    this.isPinSuccess = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.confirmationActionType = null;
    this.confirmationId = null;
    this.isGoogle2FAEnabled = false;
    this.values.username = '';
    this.values.password = '';
    this.values.code = '';
  }

  async resendSecurityCode() {
    try {
      await resendSecurityCode(BACKEND_MODULE_TYPES.OAUTH, this.confirmationId);

      runInAction(() => {
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    }
  }

  async sendAuthorizationData() {
    this.setIsLoading(true);

    try {
      const { confirmationId, confirmationType } = await loginUser({
        phone: this.values.phone,
        password: this.values.password
      });

      runInAction(() => {
        this.isLoginSuccess = true;
        this.isLoading = false;
        this.confirmationActionType = confirmationType;
        this.confirmationId = confirmationId;
        this.isGoogle2FAEnabled = confirmationType === TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH;
        this.phone = this.values.phone;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    this.setIsLoading(true);

    try {
      const { confirmation_id: confirmationId, type } = await forceChangeConfirmationTypeRequest(
        this.confirmationId,
        confirmationType,
        BACKEND_MODULE_TYPES.OAUTH
      );

      runInAction(() => {
        setConfirmationActionType(type);
        this.confirmationActionType = type;
        this.confirmationId = confirmationId;
        this.error = null;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };

  async sendCode() {
    if (this.isLoading) {
      return;
    }

    this.setIsLoading(true);
    try {
      await loginPin(this.values.code, this.confirmationId);
      runInAction(() => {
        this.isPinSuccess = true;
        this.isUserAlreadyRegistered = false;
        this.isLoading = false;
        this.confirmationId = null;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }

  async sendPasswordChange() {
    this.setIsLoading(true);
    try {
      await changePasswordUser(this.values.password);
      runInAction(() => {
        this.isPasswordChanged = true;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err;
      });
    }
  }
}

export default new AuthStore();
